<script lang="ts">
  import {onDestroy, onMount} from 'svelte';
  import {Button} from '@px/button';
  import {sd} from '@/da';
  import {eq, gt, toString} from '@/helper/num';
  import {walletContextState$} from '@/streams/web3';
  import RadioButton from '@/widgets/RadioButton.svelte';
  import {DepositToAccountType} from './utils';
  import {step$} from '../streams';
  import {
    loadData$,
    amount$,
    network$,
    tokenAddress$,
    componentStatus$,
    depositLimit$,
    amountInputConfig$,
    depositAccountType$,
    isValid$,
    currency$,
    submit$,
    submitState$,
    token$,
    setDefaultData$,
    currentCurrencySupportDepositToContract$,
    currentReceiveAddress$,
  } from './streams';
  import TransitionWrap from '../TransitionWrap.svelte';
  import CurrencySelect from './CurrencySelect.svelte';
  import NetworkSelect from './NetworkSelect.svelte';
  import DepositAmount from './DepositAmount.svelte';
  import lang from '../DepositDrawer.lang';

  export let LG;

  let choosedNetwork;
  let inputElement;

  $: ({address} = $walletContextState$.context);
  $: amountPlacehoder = eq($depositLimit$.min, $depositLimit$.max) ? `Min ${$depositLimit$.min}` : `${$depositLimit$.min} - ${$depositLimit$.max}`;
  $: currentToken = ($token$ || {}) as Token;
  $: depositCallback($submitState$);
  $: noteNetworkText = getNoteNetworkText($currency$, choosedNetwork);
  $: showMaxButton = isShowMaxButton(currentToken);

  onMount(() => {
    componentStatus$.next('visible');
    loadData$.next('');
    setDefaultData$.next($$props as any);
  });

  onDestroy(() => {
    componentStatus$.next('hidden');
  });

  function isShowMaxButton(currentToken: Token) {
    if (!currentToken) {
      return false;
    }
    try {
      return currentToken.showBalance && gt(currentToken.balance || 0, 0);
    } catch (error) {}
  }

  function getNoteNetworkText(currency, choosedNetwork) {
    const {text} = choosedNetwork || {};
    if (currency && text) {
      return `${currency}-${text}`;
    }
    return '--';
  }

  function depositCallback(submitState) {
    if (submitState?.data?.success) {
      step$.next('result');
    }
  }

  function handleDepositClick() {
    sd.track('web_click', {$screen_name: 'choose_wallet', $element_content: 'deposit_confirm'});
    submit$.next({network: $network$, amount: toString($amount$), contractAddress: $tokenAddress$, walletAddress: address, receiveAddress: $currentReceiveAddress$});
  }

  function handleMaxClick() {
    const {balance} = currentToken;
    const inputEle = inputElement.querySelector('input');
    const inputValue = parseFloat(balance || 0);
    amount$.next(inputValue);
    setTimeout(() => {
      inputEle.value = inputValue;
    }, 0);
  }

  type Token = InferObservable<typeof token$>;
</script>

<style lang="scss">
  .label {
    font-size: 14px;
    color: rgba(29, 33, 41, 0.85);
  }
  .btn-wrap :global(.submit-btn) {
    border-radius: 50px;
    height: 50px;
  }
  .light-color {
    color: #777b83;
  }
  .radio-button-group :global(label.disabled) {
    cursor: not-allowed;
    opacity: 0.5;
  }
  .max-btn {
    position: absolute;
    top: 50%;
    right: 20px;
    transform: translateY(-50%);
    &::before {
      position: absolute;
      top: 50%;
      left: -10px;
      width: 1px;
      height: 18px;
      background: var(--T4);
      transform: translateY(-50%);
      content: '';
    }
  }
  .wallet-deposit {
    padding-bottom: 40px;
  }
</style>

<TransitionWrap>
  <div class="wallet-deposit wp100">
    <h1 class="p0 T1 f32 fw2">{LG(lang.DepositFromWellet)}</h1>
    <div class="label mt32">1. {LG(lang.SelectCoin)}</div>
    <div class="mt16" on:wheel|stopPropagation>
      <CurrencySelect />
    </div>
    <div class="label mt32">2. {LG(lang.SelectNetwork)}</div>
    <div class="mt16" on:wheel|stopPropagation>
      <NetworkSelect bind:choosedNetwork />
    </div>
    <div class="label mt32">3. {LG(lang.DepositAmount)}</div>
    <div id="chainDepositAmount" class="mt16 pr" bind:this={inputElement}>
      <DepositAmount minValueEv={$depositLimit$.min} limit={$depositLimit$} placeholder="enter the deposit amount" scale={0} factor={1} {...$amountInputConfig$} bind:valueEv={$amount$} />
      <div class="max-btn f14 fw2 BLUE cp" hidden={!showMaxButton} on:click={handleMaxClick}>
        {LG(lang.Max)}
      </div>
    </div>
    <div class="light-color mt16" hidden={!currentToken.showBalance}>{LG(lang.WalletBalance)}: {currentToken.balance || '--'} {currentToken.currency || ''}</div>
    <div class="label mt32">4. {LG(lang.DepositTo)}</div>
    <div class="radio-button-group mt16">
      <RadioButton value={DepositToAccountType.spot} bind:group={$depositAccountType$}>
        <span class="to-type lh20 f14 fw2 T3">{LG(lang.SpotAccount)}</span>
      </RadioButton>
      <span hidden={$currentCurrencySupportDepositToContract$.loading || !$currentCurrencySupportDepositToContract$.data}>
        <RadioButton class="ml12" value={DepositToAccountType.contract} bind:group={$depositAccountType$}>
          <span class="to-type lh20 f14 fw2 T3">{LG(lang.ContractAccount)}</span>
        </RadioButton>
      </span>
    </div>
    <div class="f14 T3 mt12">
      <span class="T1 fw2">{LG(lang.Note)}：</span><span>{@html LG(lang.ChainDepositNotes, {Network: `<a href="javascript:void(0);">${noteNetworkText}</a>`})}</span>
    </div>
    <div class="btn-wrap mt32">
      <Button disabled={!$isValid$} loading={$submitState$ && $submitState$.loading} newRadius type="primary" class="submit-btn wp100 f16 fw2" on:click={handleDepositClick}>
        {LG(lang.Submit)}
      </Button>
    </div>
  </div>
</TransitionWrap>
